import { ChevronDownIcon, ListFilter } from 'lucide-react';
import * as React from 'react';

import { DimeButton } from './DimeButton.js';
import { DimeFilterButtonContent } from './DimeFilterButtonContent.js';
import { cn } from './lib/utils.js';

type DimeFilterButtonProps = Omit<
  React.ComponentProps<typeof DimeButton>,
  'children' | 'variant' | 'size'
> & {
  label?: string;
  /** Pass null to hide. Defaults to the ListFilter icon. */
  icon?: React.ReactNode | null;
  count?: number;
  showChevron?: boolean;
  /** Defaults to true when an icon is shown. Pass false to keep the label at every breakpoint. */
  hideLabelBelowSm?: boolean;
  accessibleName?: string;
};

/** Outline filter button: shared content plus an optional chevron for menu/popover triggers. */
export const DimeFilterButton = React.forwardRef<
  HTMLButtonElement,
  Readonly<DimeFilterButtonProps>
>(function DimeFilterButton(
  {
    label = 'Filter',
    icon,
    count = 0,
    showChevron = false,
    hideLabelBelowSm,
    accessibleName,
    className,
    'aria-label': ariaLabel,
    type = 'button',
    ...props
  },
  ref,
) {
  const engaged = count > 0;
  const name = accessibleName ?? label;
  const resolvedIcon =
    icon === null ? null : (icon ?? <ListFilter className="size-3.5" aria-hidden />);

  return (
    <DimeButton
      ref={ref}
      type={type}
      size="sm"
      variant="outline"
      className={cn('gap-1.5', className)}
      aria-label={ariaLabel ?? (engaged ? `${name}, ${count} applied` : name)}
      {...props}
    >
      <DimeFilterButtonContent
        label={label}
        icon={resolvedIcon}
        count={count}
        hideLabelBelowSm={hideLabelBelowSm}
      />
      {showChevron ? (
        <ChevronDownIcon aria-hidden className="pointer-events-none size-4 text-muted-foreground" />
      ) : null}
    </DimeButton>
  );
});

DimeFilterButton.displayName = 'DimeFilterButton';
